import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Divider, Alert, Grid, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  IconButton, Autocomplete, Chip, CircularProgress, MenuItem, Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import FactoryIcon from '@mui/icons-material/Factory';
import InventoryIcon from '@mui/icons-material/Inventory';
import GroupIcon from '@mui/icons-material/Group';

import MasProductModal from '../../../components/common/form/modal/MasProductModal';
import WorkflowAssignmentDialog from './WorkflowAssignmentDialog';
import * as autoPlanApi from '../../../lib/api/autoPlan';
import * as manufacturingApi from '../../../lib/api/manufacturing';
import * as userApi from '../../../lib/api/user';

/** 분을 사람이 읽는 시간으로. 90 -> "약 1시간 30분" */
const readableMinutes = (min) => {
  if (!min || min <= 0) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `약 ${m}분`;
  if (m === 0) return `약 ${h}시간`;
  return `약 ${h}시간 ${m}분`;
};

/**
 * 고른 워크플로우가 어떤 순서로 도는지 한 줄로 보여준다.
 *
 * 이름만 보고 고르라고 하면 그 안에 무슨 공정이 들어있는지 알 수 없어,
 * 워크플로우 관리 화면을 따로 열어 확인해야 했다.
 *
 * 공정이 많으면 줄이 길어지므로 앞의 넷만 펼치고 나머지는 수로 접는다.
 * 마우스를 올리면 전체가 나온다.
 */
const WorkflowFlow = ({ steps }) => {
  if (!steps) {
    return <Typography variant="caption" color="text.disabled">공정 불러오는 중…</Typography>;
  }
  if (steps.length === 0) {
    return <Typography variant="caption" color="warning.main">등록된 공정이 없습니다</Typography>;
  }

  const names = steps.map((s) => s.processNm || s.procNm || '이름없음');
  const totalMin = steps.reduce((sum, s) => sum + (Number(s.estDurationMin) || 0), 0);
  const shown = names.slice(0, 4);
  const rest = names.length - shown.length;

  return (
    <Tooltip
      title={names.map((n, i) => `${i + 1}. ${n}`).join('   ')}
      placement="top-start"
      enterTouchDelay={0}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', cursor: 'help' }}>
        {shown.join(' → ')}
        {rest > 0 && ` 외 ${rest}단계`}
        {' · '}
        {steps.length}단계
        {totalMin > 0 && ` · ${readableMinutes(totalMin)}`}
      </Typography>
    </Tooltip>
  );
};

/**
 * 발주 단계 생산계획 미리보기.
 *
 * 원가산출 내역을 전부 올려놓고, 뺄 것은 사용자가 휴지통으로 뺀다.
 * 숨겨서 안 만드는 것과 사용자가 뺀 것은 다르다. 앞엣것은 나중에 왜 없는지 물어보게 된다.
 *
 * 기타 항목은 제외 칸에서 시작한다. 제품이 없어서 그대로는 만들 수 없는데,
 * 그중에 실제로 만드는 것이 섞여 있어서 감추지 않고 제품을 고를 수 있게 둔다.
 */
const AutoPlanPreviewDialog = ({ open, prjUuid, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [head, setHead] = useState(null);
  const [items, setItems] = useState([]);
  const [excluded, setExcluded] = useState([]);

  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);

  // 워크플로우 UUID -> 공정 목록. 고른 것이 어떤 흐름인지 보여주려고 받아둔다.
  //
  // 목록 API 는 단계 수만 주고 공정까지는 안 준다. 그래서 고른 것만 하나씩 더 부른다.
  // 한 번 받은 것은 여기 남겨두고 다시 부르지 않는다 — 줄마다 같은 워크플로우를 고르는 일이 잦다.
  const [workflowSteps, setWorkflowSteps] = useState({});

  // 제품 지정 대상. 제외 칸의 기타 항목을 되살릴 때 쓴다.
  const [productTarget, setProductTarget] = useState(null);

  // 공정별 담당자를 들여다볼 워크플로우. { uuid, name, date }
  const [assignTarget, setAssignTarget] = useState(null);

  const load = useCallback(async () => {
    if (!open || !prjUuid) return;
    setLoading(true);
    setError(null);
    try {
      const [previewRes, workflowRes, userRes] = await Promise.all([
        autoPlanApi.previewAutoPlans(prjUuid),
        manufacturingApi.getActiveWorkflows(),
        userApi.getUsers(),
      ]);

      const data = previewRes?.data?.data;
      if (!data || !data.available) {
        // 띄우지 않아도 되는 상황이다. 조용히 닫는다.
        onClose();
        return;
      }

      setHead(data);
      setItems((data.items || []).map((row) => ({
        ...row,
        planStartDate: data.planStartDate || '',
        planEndDate: data.planEndDate || '',
        workflowUuid: data.workflowUuid || '',
        designManagerUuid: data.designManagerUuid || '',
        designManagerName: data.designManagerName || '',
        productionManagerUuid: data.productionManagerUuid || '',
        productionManagerName: data.productionManagerName || '',
        qcManagerUuid: data.qcManagerUuid || '',
        qcManagerName: data.qcManagerName || '',
      })));
      setExcluded(data.excluded || []);

      setWorkflows(workflowRes?.data?.data || []);
      setUsers(userRes?.data?.list || []);
    } catch (err) {
      setError(err?.response?.data?.message || '생산계획 미리보기를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [open, prjUuid, onClose]);

  useEffect(() => { load(); }, [load]);

  // 화면에 쓰이는 워크플로우의 공정을 받아둔다. 아직 안 받은 것만 부른다.
  useEffect(() => {
    const needed = [...new Set(items.map((r) => r.workflowUuid).filter(Boolean))]
      .filter((uuid) => workflowSteps[uuid] === undefined);
    if (needed.length === 0) return;

    let alive = true;
    // 못 받아도 화면은 그대로 돌아가야 한다. 흐름만 안 보일 뿐이다.
    // 실패한 것도 빈 배열로 남겨서 같은 것을 계속 다시 부르지 않게 한다.
    Promise.all(needed.map((uuid) =>
      manufacturingApi.getWorkflow(uuid)
        .then((res) => [uuid, res?.data?.data?.details || []])
        .catch(() => [uuid, []])
    )).then((pairs) => {
      if (alive) setWorkflowSteps((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => { alive = false; };
  }, [items, workflowSteps]);

  const changeRow = (expUuid, field, value) => {
    setItems((prev) => prev.map((row) =>
      row.expUuid === expUuid ? { ...row, [field]: value } : row));
  };

  const changeManager = (expUuid, kind, user) => {
    setItems((prev) => prev.map((row) => row.expUuid === expUuid
      ? { ...row, [`${kind}Uuid`]: user?.usrUuid || '', [`${kind}Name`]: user?.usrNm || '' }
      : row));
  };

  /** 전 행에 한 번에. 한 줄씩 고르게 하면 손이 너무 많이 간다. */
  const applyToAll = (patch) => setItems((prev) => prev.map((row) => ({ ...row, ...patch })));

  const bin = (expUuid) => {
    const row = items.find((r) => r.expUuid === expUuid);
    if (!row) return;
    setItems((prev) => prev.filter((r) => r.expUuid !== expUuid));
    setExcluded((prev) => [...prev, { ...row, excludeReason: '' }]);
  };

  const restore = (row) => {
    // 제품이 없으면 되살릴 수 없다. 없는 제품으로 계획이 만들어진다.
    if (!row.productId || row.masterTyp !== 'N') {
      setProductTarget(row);
      return;
    }
    setExcluded((prev) => prev.filter((r) => r.expUuid !== row.expUuid));
    setItems((prev) => [...prev, {
      ...row,
      excludeReason: '',
      planStartDate: head?.planStartDate || '',
      planEndDate: head?.planEndDate || '',
      workflowUuid: head?.workflowUuid || '',
      designManagerUuid: head?.designManagerUuid || '',
      designManagerName: head?.designManagerName || '',
      productionManagerUuid: head?.productionManagerUuid || '',
      productionManagerName: head?.productionManagerName || '',
      qcManagerUuid: head?.qcManagerUuid || '',
      qcManagerName: head?.qcManagerName || '',
    }]);
  };

  const handleProductPicked = (product) => {
    const target = productTarget;
    setProductTarget(null);
    if (!target) return;

    setExcluded((prev) => prev.filter((r) => r.expUuid !== target.expUuid));
    setItems((prev) => [...prev, {
      ...target,
      // 제품을 지정했으니 이제 제품 줄과 같다.
      masterTyp: 'N',
      productId: product.productUuid,
      productName: product.productNm,
      productSpec: product.modelNm || '',
      excludeReason: '',
      planStartDate: head?.planStartDate || '',
      planEndDate: head?.planEndDate || '',
      workflowUuid: head?.workflowUuid || '',
      designManagerUuid: head?.designManagerUuid || '',
      designManagerName: head?.designManagerName || '',
      productionManagerUuid: head?.productionManagerUuid || '',
      productionManagerName: head?.productionManagerName || '',
      qcManagerUuid: head?.qcManagerUuid || '',
      qcManagerName: head?.qcManagerName || '',
    }]);
  };

  /** 만들 수 없는 줄이 남아 있으면 무엇이 비었는지 알려준다. */
  const firstProblem = () => {
    for (let i = 0; i < items.length; i += 1) {
      const row = items[i];
      const at = `${i + 1}번째 (${row.productName || '이름 없음'})`;
      if (!row.productId) return `${at} 항목에 제품이 없습니다.`;
      if (!row.workflowUuid) return `${at} 항목에 공정 워크플로우가 없습니다.`;
      if (!row.designManagerUuid || !row.productionManagerUuid || !row.qcManagerUuid) {
        return `${at} 항목에 담당자가 비어 있습니다.`;
      }
      if (!row.planStartDate || !row.planEndDate) return `${at} 항목에 기간이 없습니다.`;
      if (row.planEndDate < row.planStartDate) return `${at} 항목의 종료일이 시작일보다 빠릅니다.`;
    }
    return '';
  };

  const handleCreate = async () => {
    const problem = firstProblem();
    if (problem) {
      setError(problem);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = items.map((row) => ({
        projectId: prjUuid,
        productId: row.productId,
        productName: row.productName,
        planQty: Number(row.planQty) || 1,
        planStartDate: row.planStartDate,
        planEndDate: row.planEndDate,
        workflowUuid: row.workflowUuid,
        designManagerUuid: row.designManagerUuid,
        designManagerName: row.designManagerName,
        productionManagerUuid: row.productionManagerUuid,
        productionManagerName: row.productionManagerName,
        qcManagerUuid: row.qcManagerUuid,
        qcManagerName: row.qcManagerName,
        remarks: '발주 단계에서 원가산출 내역으로 자동 생성',
      }));

      const res = await autoPlanApi.createAutoPlans(payload);
      if (res?.data?.success) {
        onCreated(items.length);
        onClose();
      } else {
        setError(res?.data?.message || '생산계획 생성에 실패했습니다.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || '생산계획 생성에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const empBox = (row, kind, label) => (
    <Autocomplete
      size="small"
      options={users}
      value={users.find((u) => u.usrUuid === row[`${kind}Uuid`]) || null}
      getOptionLabel={(o) => o?.usrNm || ''}
      isOptionEqualToValue={(o, v) => o.usrUuid === v?.usrUuid}
      onChange={(e, value) => changeManager(row.expUuid, kind, value)}
      renderInput={(params) => (
        <TextField {...params} placeholder={label} variant="standard" />
      )}
      sx={{ minWidth: 96 }}
    />
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FactoryIcon color="primary" />
            <Typography variant="h6">발주 단계 — 생산계획 미리보기</Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">프로젝트</Typography>
                  <Typography variant="body2">
                    {head?.projectNumber ? `${head.projectNumber} ` : ''}{head?.projectName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">기간</Typography>
                  <Typography variant="body2">
                    {head?.planStartDate || '-'} (오늘) ~ {head?.planEndDate || '수주품의서에 완료일이 없습니다'}
                  </Typography>
                </Grid>
              </Grid>

              {!head?.planEndDate && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  수주품의서에 계약 완료일이 없어 종료일을 채우지 못했습니다. 줄마다 직접 넣어주세요.
                </Alert>
              )}
              {!head?.workflowUuid && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  기본 워크플로우가 지정되어 있지 않습니다. 공정 관리 &gt; 워크플로우에서 별을 눌러
                  지정해두면 다음부터 자동으로 채워집니다.
                </Alert>
              )}
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              {/* 일괄 지정 */}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Typography variant="body2" fontWeight={600}>일괄 지정</Typography>
                  </Grid>
                  <Grid item xs={12} sm={2.5}>
                    <TextField
                      select fullWidth size="small" label="워크플로우" value=""
                      onChange={(e) => applyToAll({ workflowUuid: e.target.value })}
                    >
                      {/* 고르기 전에 몇 단계짜리인지 보이게 한다. 단계 수는 목록에 이미 들어 있다. */}
                      {workflows.map((w) => (
                        <MenuItem key={w.workflowUuid} value={w.workflowUuid}>
                          {w.workflowNm}
                          {w.stepCount > 0 && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {w.stepCount}단계
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {[
                    ['designManager', '설계'],
                    ['productionManager', '생산'],
                    ['qcManager', 'QC'],
                  ].map(([kind, label]) => (
                    <Grid item xs={12} sm={2.5} key={kind}>
                      <Autocomplete
                        size="small"
                        options={users}
                        getOptionLabel={(o) => o?.usrNm || ''}
                        onChange={(e, value) => applyToAll({
                          [`${kind}Uuid`]: value?.usrUuid || '',
                          [`${kind}Name`]: value?.usrNm || '',
                        })}
                        renderInput={(params) => <TextField {...params} label={label} />}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* 만들 항목 */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                만들 항목 ({items.length})
              </Typography>
              <Box sx={{ overflowX: 'auto', mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>제품명</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 90 }}>수량</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 150 }}>시작일</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 150 }}>종료일</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>워크플로우</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>설계</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>생산</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>QC</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 56 }}>제외</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.disabled' }}>
                          만들 항목이 없습니다.
                        </TableCell>
                      </TableRow>
                    ) : items.map((row) => (
                      <TableRow key={row.expUuid} hover>
                        <TableCell>{row.productName}</TableCell>
                        <TableCell>
                          <TextField
                            type="number" size="small" variant="standard"
                            value={row.planQty ?? 1}
                            onChange={(e) => changeRow(row.expUuid, 'planQty', e.target.value)}
                            inputProps={{ min: 1 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date" size="small" variant="standard"
                            value={row.planStartDate || ''}
                            onChange={(e) => changeRow(row.expUuid, 'planStartDate', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date" size="small" variant="standard"
                            value={row.planEndDate || ''}
                            onChange={(e) => changeRow(row.expUuid, 'planEndDate', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select fullWidth size="small" variant="standard"
                            value={row.workflowUuid || ''}
                            onChange={(e) => changeRow(row.expUuid, 'workflowUuid', e.target.value)}
                          >
                            {workflows.map((w) => (
                              <MenuItem key={w.workflowUuid} value={w.workflowUuid}>
                                {w.workflowNm}
                                {w.stepCount > 0 && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {w.stepCount}단계
                                  </Typography>
                                )}
                              </MenuItem>
                            ))}
                          </TextField>
                          {row.workflowUuid && (
                            <Box sx={{ mt: 0.5 }}>
                              <WorkflowFlow steps={workflowSteps[row.workflowUuid]} />
                              <Button
                                size="small"
                                startIcon={<GroupIcon fontSize="small" />}
                                sx={{ mt: 0.25, p: 0, minWidth: 0 }}
                                onClick={() => setAssignTarget({
                                  uuid: row.workflowUuid,
                                  name: workflows.find((w) => w.workflowUuid === row.workflowUuid)?.workflowNm || '',
                                  // 그 줄의 시작일 기준으로 본다. 줄마다 일정이 다를 수 있다.
                                  date: row.planStartDate || '',
                                })}
                              >
                                담당자
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{empBox(row, 'designManager', '설계')}</TableCell>
                        <TableCell>{empBox(row, 'productionManager', '생산')}</TableCell>
                        <TableCell>{empBox(row, 'qcManager', 'QC')}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="제외">
                            <IconButton size="small" onClick={() => bin(row.expUuid)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* 제외한 항목 */}
              {excluded.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    제외한 항목 ({excluded.length})
                  </Typography>
                  <Paper variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {excluded.map((row) => (
                          <TableRow key={row.expUuid}>
                            <TableCell sx={{ minWidth: 160 }}>{row.productName}</TableCell>
                            <TableCell>
                              {row.excludeReason ? (
                                <Chip
                                  size="small" variant="outlined" color="warning"
                                  icon={<InventoryIcon />}
                                  label={row.excludeReason}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  사용자가 제외했습니다
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ width: 160 }}>
                              <Button size="small" startIcon={<UndoIcon />} onClick={() => restore(row)}>
                                {row.masterTyp === 'N' && row.productId ? '되돌리기' : '제품 지정'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </>
              )}
            </>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {items.length}건 생성
          </Typography>
          <Box>
            <Button onClick={onClose} color="inherit" disabled={saving} sx={{ mr: 1 }}>
              건너뛰기
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={saving || loading || items.length === 0}
            >
              {saving ? '만드는 중...' : `${items.length}건 생성`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <WorkflowAssignmentDialog
        open={Boolean(assignTarget)}
        workflowUuid={assignTarget?.uuid}
        workflowName={assignTarget?.name}
        workDate={assignTarget?.date}
        onClose={() => setAssignTarget(null)}
      />

      <MasProductModal
        title="제품 선택"
        open={Boolean(productTarget)}
        handleClose={() => setProductTarget(null)}
        selectedProduct={handleProductPicked}
      />
    </>
  );
};

export default AutoPlanPreviewDialog;
